import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import Modal from './Modal';
import './Slide5.css';

const Slide5Component = () => {
  const [typedText, setTypedText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (cardType) => {
    setModalContent(getModalContent(cardType));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getModalContent = (type) => {
    const contents = {
      proxy: {
        title: 'Proxy MITM Intégré',
        icon: 'bi bi-shield-lock',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-gear-fill"></i> Un bouclier intelligent entre vous et le web</h3>
              <p>
                Le proxy MITM (Man-In-The-Middle) intégré à MarIAne est bien plus qu'un simple filtre. C'est un
                système de sécurité intelligent qui s'interpose entre votre navigateur et les sites web que vous
                visitez, analysant et filtrant chaque requête en temps réel pour vous protéger des menaces tout
                en préservant une expérience de navigation fluide et naturelle.
              </p>
              <p>
                Contrairement aux bloqueurs de publicité classiques qui se contentent de masquer des éléments
                après leur chargement, notre proxy intercepte les requêtes avant même qu'elles ne partent de
                votre navigateur. Cela signifie que les trackers, publicités et scripts malveillants ne sont
                jamais téléchargés, économisant votre bande passante et protégeant votre vie privée de manière
                plus efficace.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&q=80"
              alt="Sécurité réseau et protection"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-funnel"></i> Filtrage intelligent en temps réel</h3>
              <p>
                Le filtrage du contenu n'est pas binaire chez MarIAne. Notre système utilise l'intelligence
                artificielle pour analyser chaque requête dans son contexte et prendre des décisions intelligentes.
                Un script de suivi sur un site d'actualités sera bloqué, mais le même domaine utilisé pour des
                fonctionnalités légitimes sera autorisé. C'est une protection intelligente, pas aveugle.
              </p>
              <p>
                Cette approche contextuelle élimine les faux positifs qui rendent certains sites inutilisables
                avec les bloqueurs traditionnels. MarIAne comprend la différence entre un tracker publicitaire
                et une bibliothèque JavaScript légitime, entre un cookie de session nécessaire et un cookie
                de profilage intrusif. Vous êtes protégé sans sacrifice de fonctionnalité.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Le proxy MITM de MarIAne est votre garde du corps numérique :
              toujours vigilant, jamais intrusif, protégeant votre vie privée sans compromettre votre
              expérience de navigation."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-shield-check"></i> Blocage automatique des trackers</h3>
              <p>
                Les trackers sont partout sur le web moderne, collectant vos habitudes de navigation pour
                construire des profils détaillés de vos intérêts, comportements et préférences. MarIAne bloque
                automatiquement ces mouchards numériques avant même qu'ils ne puissent s'exécuter. Nos listes
                de blocage sont constamment mises à jour et enrichies par l'IA qui identifie de nouveaux
                patterns de tracking.
              </p>
              <p>
                Le résultat ? Vous naviguez anonymement sans laisser de traces numériques derrière vous.
                Les régies publicitaires ne peuvent plus vous suivre d'un site à l'autre, les data brokers
                ne peuvent plus construire votre profil, et votre vie privée reste vraiment privée. C'est
                la navigation comme elle devrait être : libre et sans surveillance.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=800&q=80"
              alt="Protection contre le tracking"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-fingerprint"></i> Anti-fingerprinting avancé</h3>
              <p>
                Le fingerprinting est une technique de tracking particulièrement insidieuse qui ne repose
                pas sur les cookies mais sur les caractéristiques uniques de votre navigateur et de votre
                système. MarIAne utilise des techniques avancées de randomisation et de normalisation pour
                rendre votre empreinte digitale indiscernable dans la masse, vous protégeant contre cette
                forme de tracking même quand les cookies sont désactivés.
              </p>
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-sliders"></i> Contrôle total sur vos données</h3>
              <p>
                Le proxy vous donne un contrôle granulaire sur toutes les données qui entrent et sortent
                de votre navigateur. Vous pouvez voir exactement quelles informations sont partagées avec
                quels domaines, bloquer sélectivement certains types de requêtes, et même injecter vos
                propres règles de filtrage personnalisées. C'est votre navigateur, c'est votre vie privée,
                ce sont vos règles.
              </p>
            </div>
          </>
        )
      },
      protection: {
        title: 'Protection Multi-Niveaux',
        icon: 'bi bi-layers',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-stack"></i> Une défense en profondeur</h3>
              <p>
                La protection multi-niveaux de MarIAne repose sur le principe de défense en profondeur :
                plusieurs couches de sécurité qui se complètent pour créer un bouclier pratiquement
                impénétrable. Chaque niveau cible des menaces spécifiques et utilise des techniques
                d'analyse différentes, garantissant qu'une menace qui échapperait à un niveau sera
                interceptée par le suivant.
              </p>
              <p>
                Cette architecture en couches n'est pas qu'une question de redondance. Chaque niveau
                apporte sa propre expertise et ses propres algorithmes de détection. Le résultat est
                une protection qui s'adapte aux menaces évolutives sans nécessiter de mises à jour
                constantes de votre part. La sécurité devrait être automatique, et c'est exactement
                ce que nous offrons.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80"
              alt="Cybersécurité et protection multicouche"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-bug"></i> Détection de spam et malware</h3>
              <p>
                La première ligne de défense de MarIAne cible le spam et les malwares avant même qu'ils
                ne puissent s'exécuter. Notre système utilise une combinaison de signatures connues,
                d'analyse heuristique et de machine learning pour identifier les menaces émergentes.
                Les sites de phishing sont bloqués, les téléchargements malveillants sont interceptés,
                et les scripts dangereux sont neutralisés automatiquement.
              </p>
              <p>
                Cette protection fonctionne en temps réel et s'améliore continuellement grâce à
                l'apprentissage automatique. Chaque nouvelle menace identifiée enrichit notre base
                de connaissances, mais toujours localement sur votre machine. Vous bénéficiez d'une
                protection collective sans jamais partager vos données de navigation avec nos serveurs.
                C'est la sécurité collaborative respectueuse de la vie privée.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La protection multi-niveaux, c'est comme avoir plusieurs
              gardes du corps experts chacun dans un domaine différent. Une menace qui passerait entre
              les mailles d'un filet sera attrapée par le suivant."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-fingerprint"></i> Anti-fingerprinting et protection du tracking</h3>
              <p>
                Au-delà du simple blocage de cookies, MarIAne protège contre toutes les formes de
                tracking, y compris les plus sophistiquées. Le fingerprinting de canvas, les
                supercookies, le tracking par batterie ou même par accéléromètre : toutes ces
                techniques invasives sont neutralisées. Nous randomisons ou normalisons les
                informations que votre navigateur expose pour rendre le tracking impossible sans
                compromettre la fonctionnalité des sites légitimes.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&q=80"
              alt="Anonymat et vie privée"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-code-slash"></i> Contrôle des scripts et headers</h3>
              <p>
                MarIAne vous donne un contrôle granulaire sur l'exécution des scripts JavaScript et
                sur les headers HTTP. Les scripts potentiellement dangereux peuvent être bloqués ou
                sandboxés, et les headers révélant des informations sensibles sur votre système sont
                nettoyés automatiquement. Vous décidez quel niveau de protection vous voulez, du mode
                strict qui bloque tout par défaut au mode équilibré qui autorise les sites de confiance.
              </p>
            </div>
          </>
        )
      },
      transparency: {
        title: 'Transparence',
        icon: 'bi bi-eye',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-display"></i> Voir ce qui se passe vraiment</h3>
              <p>
                La transparence est au cœur de la philosophie de MarIAne. Nous croyons que vous avez
                le droit de savoir exactement ce qui se passe quand vous naviguez sur le web. Notre
                interface de visualisation en temps réel vous montre chaque requête bloquée, chaque
                tracker neutralisé, chaque tentative de fingerprinting déjouée. Vous n'êtes plus dans
                le noir, vous voyez exactement comment votre vie privée est protégée.
              </p>
              <p>
                Cette visibilité n'est pas qu'informative, elle est éducative. En voyant combien de
                trackers un site utilise, combien de fois il tente de vous profiler, vous développez
                une conscience aigüe de l'économie de surveillance qui domine le web moderne. Cette
                prise de conscience est le premier pas vers une navigation plus éclairée et plus sûre.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              alt="Visualisation et analyse de données"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-graph-up"></i> Statistiques détaillées par site</h3>
              <p>
                Chaque site que vous visitez génère des statistiques détaillées dans MarIAne. Vous
                pouvez voir combien de trackers ont été bloqués, combien de requêtes tierces ont été
                interceptées, quels domaines tentent de vous suivre, et même obtenir un score de
                confidentialité pour chaque site. Ces données vous permettent de prendre des décisions
                éclairées sur les sites en qui vous voulez avoir confiance.
              </p>
              <p>
                Les statistiques sont présentées de manière claire et visuelle : graphiques,
                chronologies, comparaisons. Vous pouvez voir l'évolution du comportement d'un site
                au fil du temps, comparer différents sites du même secteur, et même exporter vos
                données pour vos propres analyses. C'est votre navigation, ce sont vos données,
                vous devriez pouvoir les analyser comme bon vous semble.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La vraie transparence n'est pas de promettre qu'on respecte
              votre vie privée. C'est de vous donner les outils pour vérifier par vous-même et voir
              exactement ce qui se passe."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-sliders"></i> Contrôle granulaire par domaine</h3>
              <p>
                Tous les sites ne sont pas égaux en termes de confiance et de fonctionnalité. MarIAne
                vous permet de définir des règles spécifiques pour chaque domaine. Vous pouvez autoriser
                certains trackers sur un site que vous jugez de confiance tout en bloquant tout sur
                un site douteux. Vous pouvez créer des listes blanches, des listes noires, et même
                des règles conditionnelles complexes.
              </p>
              <p>
                Ce contrôle granulaire signifie que vous n'avez jamais à choisir entre sécurité et
                fonctionnalité. Un site bancaire peut avoir besoin d'exécuter plus de scripts qu'un
                blog personnel, et MarIAne vous laisse faire cette distinction. C'est votre navigateur,
                ce sont vos règles, configurez-le exactement comme vous le voulez.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80"
              alt="Contrôle et gestion personnalisée"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-clock-history"></i> Historique et journalisation</h3>
              <p>
                MarIAne maintient un journal détaillé de toutes les protections activées, mais
                contrairement aux journaux de navigation traditionnels qui peuvent devenir un risque
                pour la vie privée, notre système stocke tout localement et chiffré. Vous pouvez
                consulter l'historique de vos protections, analyser les tendances, et même exporter
                des rapports, mais personne d'autre ne peut accéder à ces informations. La transparence
                pour vous, l'opacité pour les autres.
              </p>
            </div>
          </>
        )
      }
    };

    return contents[type];
  };

  useEffect(() => {
    const tl = gsap.timeline();

    tl.set('.slide5-header', { opacity: 1 });

    tl.add(() => {
      const text = "Comment fonctionne le navigateur sécurisé ?";
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);
    }, '+=0.8');

    tl.to('.slide5-search-button', {
      scale: 1.1,
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.8)',
      duration: 0.3,
      repeat: 1,
      yoyo: true
    }, '+=2');

    tl.fromTo('.slide5-main-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    , '+=0.3');

    tl.fromTo('.slide5-separator',
      { opacity: 0, scaleX: 0 },
      { opacity: 1, scaleX: 1, duration: 0.8, ease: 'power2.inOut' }
    , '-=0.4');

    tl.fromTo('.browser-section',
      { opacity: 0, y: 50, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.15, ease: 'power2.out' }
    , '+=0.2');

    return () => tl.kill();
  }, []);

  return (
    <div className="slide-content slide-5-transition">
      <header className="slide5-header">
        <div className="slide5-header-content">
          <div className="slide5-logo-small">
            MAR<span className="neon-text">IA</span>NE
          </div>
          <form className="slide5-search-form">
            <div className="slide5-search-box">
              <input
                type="text"
                className="slide5-search-input"
                placeholder="Rechercher..."
                value={typedText}
                readOnly
              />
              <button type="button" className="slide5-search-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </header>

      <div className="slide5-content">
        <div className="slide5-main-title">
          <h1 className="slide5-h1">Le Navigateur</h1>
          <p className="slide5-sub">Technologies Avancées de Protection et Filtrage</p>
        </div>

        <div className="slide5-separator"></div>

        <div className="browser-sections">
          <div className="browser-section" onClick={() => openModal('proxy')} style={{ cursor: 'pointer' }}>
            <div className="section-header">
              <i className="bi bi-shield-lock"></i>
              <h3>Proxy MITM Intégré</h3>
            </div>
            <ul className="section-list">
              <li>Filtrage intelligent du contenu en temps réel</li>
              <li>Blocage automatique des trackers et publicités</li>
              <li>Anti-fingerprinting avancé</li>
              <li>Contrôle total sur les données échangées</li>
            </ul>
          </div>

          <div className="browser-section" onClick={() => openModal('protection')} style={{ cursor: 'pointer' }}>
            <div className="section-header">
              <i className="bi bi-layers"></i>
              <h3>Protection Multi-Niveaux</h3>
            </div>
            <ul className="section-list">
              <li>Spam & Malware : Détection et blocage automatique</li>
              <li>Fingerprinting : Protection contre le tracking avancé</li>
              <li>Trackers : Élimination des mouchards publicitaires</li>
              <li>Headers : Nettoyage des métadonnées sensibles</li>
              <li>Scripts : Contrôle fin de l'exécution JavaScript</li>
            </ul>
          </div>

          <div className="browser-section" onClick={() => openModal('transparency')} style={{ cursor: 'pointer' }}>
            <div className="section-header">
              <i className="bi bi-eye"></i>
              <h3>Transparence</h3>
            </div>
            <ul className="section-list">
              <li>Visualisation en temps réel des requêtes bloquées</li>
              <li>Statistiques détaillées par site</li>
              <li>Contrôle granulaire par domaine</li>
            </ul>
          </div>
        </div>
      </div>

      {modalContent && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalContent.title}
          icon={modalContent.icon}
          content={modalContent.content}
        />
      )}
    </div>
  );
};

export default Slide5Component;
